class ProductAddons extends HTMLElement {
  #abortController = new AbortController();
  #section = null;

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.#section = this.closest('.shopify-section');

    const { signal } = this.#abortController;

    this.addEventListener('change', this.#handleChange, { signal });
    this.querySelector('[data-addon-toggle]')?.addEventListener('click', this.#toggleItems, { signal });

    this.querySelectorAll('[data-addon-row]').forEach((row) => {
      const select = row.querySelector('[data-addon-variant]');
      if (select instanceof HTMLSelectElement) {
        this.#applySelectedVariant(row, select);
      }

      this.#updateSelectedState(row);
    });

    this.#syncDynamicCheckout();
  }

  disconnectedCallback() {
    this.#abortController.abort();

    const section = this.#section;
    requestAnimationFrame(() => {
      if (!section?.querySelector('product-addons[data-has-active-selection="true"]')) {
        section?.classList.remove('product-addons-has-active-selection');
      }
    });
  }

  /**
   * Public API used by product-form.js.
   * @returns {Array<{variantId: string, quantity: number, role?: 'gift'}>}
   */
  getSelectedItems() {
    const items = [];

    this.querySelectorAll('[data-addon-row]').forEach((row) => {
      if (!(row instanceof HTMLElement) || row.dataset.available !== 'true') return;

      const isGift = row.hasAttribute('data-addon-gift');
      const checkbox = row.querySelector('[data-addon-checkbox]');

      if (!isGift) {
        if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked || checkbox.disabled) return;
      }

      const variantId = row.dataset.variantId;
      if (!variantId) return;

      items.push({
        variantId,
        quantity: 1,
        ...(isGift ? { role: 'gift' } : {}),
      });
    });

    return items;
  }

  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const row = target.closest('[data-addon-row]');
    if (!(row instanceof HTMLElement)) return;

    if (target.matches('[data-addon-variant]') && target instanceof HTMLSelectElement) {
      this.#applySelectedVariant(row, target);
    }

    this.#updateSelectedState(row);
    this.#syncDynamicCheckout();
  };

  #applySelectedVariant(row, select) {
    const option = select.options[select.selectedIndex];
    if (!option) return;

    row.dataset.variantId = option.value;
    row.dataset.available = option.dataset.available === 'true' ? 'true' : 'false';

    const price = row.querySelector('[data-addon-price]');
    if (price && option.dataset.price) {
      price.textContent = option.dataset.price;
    }

    const image = row.querySelector('.product-addon__image');
    if (image instanceof HTMLImageElement && option.dataset.image) {
      image.src = option.dataset.image;
      image.removeAttribute('srcset');
    }

    const checkbox = row.querySelector('[data-addon-checkbox]');
    if (checkbox instanceof HTMLInputElement) {
      const unavailable = row.dataset.available !== 'true';
      checkbox.disabled = unavailable;
      if (unavailable) checkbox.checked = false;
      row.classList.toggle('product-addon--unavailable', unavailable);
    }
  }

  #updateSelectedState(row) {
    const isGift = row.hasAttribute('data-addon-gift');
    const checkbox = row.querySelector('[data-addon-checkbox]');
    const selected = isGift || (checkbox instanceof HTMLInputElement && checkbox.checked);

    row.dataset.selected = selected ? 'true' : 'false';
  }

  #syncDynamicCheckout() {
    const hideDynamicCheckout = this.dataset.hideDynamicCheckout === 'true';
    const hasGift = Boolean(this.querySelector('[data-addon-gift][data-available="true"]'));
    const hasSelectedPaidAddon = Boolean(this.querySelector('[data-addon-checkbox]:checked:not(:disabled)'));
    const hasActiveSelection = hasGift || hasSelectedPaidAddon;

    this.dataset.hasActiveSelection = hasActiveSelection ? 'true' : 'false';

    if (!hideDynamicCheckout || !this.#section) return;

    this.#section.classList.toggle('product-addons-has-active-selection', hasActiveSelection);
  }

  #toggleItems = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', expanded ? 'false' : 'true');

    this.querySelectorAll('[data-addon-collapsible]').forEach((item) => {
      if (item instanceof HTMLElement) {
        item.classList.toggle('product-addon--hidden', expanded);
      }
    });

    const showMore = button.querySelector('[data-show-more-text]');
    const showLess = button.querySelector('[data-show-less-text]');

    if (showMore instanceof HTMLElement) showMore.hidden = !expanded;
    if (showLess instanceof HTMLElement) showLess.hidden = expanded;
  };
}

if (!customElements.get('product-addons')) {
  customElements.define('product-addons', ProductAddons);
}
